import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getQuizForPlay, checkAnswers } from '../utils/api';
import { isValidQuizId } from '../utils/sanitize';

function PlayQuiz() {
  const { quizId } = useParams();
  const navigate = useNavigate();
  
  // Quiz state
  const [quiz, setQuiz] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  // Game state
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [answers, setAnswers] = useState({});
  const [showResult, setShowResult] = useState(false);
  const [currentResult, setCurrentResult] = useState(null);
  const [quizComplete, setQuizComplete] = useState(false);
  const [finalResults, setFinalResults] = useState(null);
  const [checking, setChecking] = useState(false);

  // Fetch quiz for playing
  const fetchQuiz = useCallback(async () => {
    if (!isValidQuizId(quizId)) {
      setError('Invalid quiz ID');
      setLoading(false);
      return;
    }

    try {
      const data = await getQuizForPlay(quizId);
      if (!data.questions || data.questions.length === 0) {
        setError('This quiz has no questions yet');
        setLoading(false);
        return;
      }
      setQuiz(data);
      setQuestions(data.questions);
    } catch (err) {
      console.error('Error fetching quiz:', err);
      if (err.status === 404) {
        setError('Quiz not found');
      } else {
        setError(err.message || 'Failed to load quiz');
      }
    } finally {
      setLoading(false);
    }
  }, [quizId]);

  useEffect(() => {
    fetchQuiz();
  }, [fetchQuiz]);

  // Handle answer selection
  const handleAnswerSelect = (answer) => {
    if (showResult) return;
    
    setSelectedAnswer(answer);
    setAnswers(prev => ({
      ...prev,
      [questions[currentQuestionIndex].id]: answer
    }));
  };

  // Submit answer and move to next question
  const handleSubmitAnswer = async () => {
    if (!selectedAnswer) return;

    // Store the answer
    const newAnswers = {
      ...answers,
      [questions[currentQuestionIndex].id]: selectedAnswer
    };
    setAnswers(newAnswers);

    // Check if this is the last question
    if (currentQuestionIndex >= questions.length - 1) {
      // Quiz complete - check all answers
      setChecking(true);
      try {
        const results = await checkAnswers(quizId, newAnswers);
        setFinalResults(results);
        
        // Find current question result
        const currentQResult = results.results.find(
          r => r.question_id === questions[currentQuestionIndex].id
        );
        setCurrentResult(currentQResult);
        setShowResult(true);
        
      } catch (err) {
        console.error('Error checking answers:', err);
        setError('Failed to check answers');
      } finally {
        setChecking(false);
      }
    } else {
      // Not the last question - need to check just this answer
      setChecking(true);
      try {
        const results = await checkAnswers(quizId, newAnswers);
        const currentQResult = results.results.find(
          r => r.question_id === questions[currentQuestionIndex].id
        );
        setCurrentResult(currentQResult);
        setShowResult(true);
      } catch (err) {
        console.error('Error checking answer:', err);
        // Continue anyway - we can show result at the end
        setShowResult(true);
      } finally {
        setChecking(false);
      }
    }
  };

  // Move to next question
  const handleNextQuestion = () => {
    if (currentQuestionIndex < questions.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
      setSelectedAnswer(answers[questions[currentQuestionIndex + 1]?.id] || null);
      setShowResult(false);
      setCurrentResult(null);
    } else {
      setQuizComplete(true);
    }
  };

  // Get option class based on result
  const getOptionClass = (letter) => {
    if (!showResult || !currentResult) return '';
    
    if (letter === currentResult.correct_answer) return 'correct';
    if (letter === selectedAnswer && letter !== currentResult.correct_answer) return 'incorrect';
    return 'dimmed';
  };

  // Restart quiz
  const handleRestart = () => {
    setCurrentQuestionIndex(0);
    setSelectedAnswer(null);
    setAnswers({});
    setShowResult(false);
    setCurrentResult(null);
    setQuizComplete(false);
    setFinalResults(null);
  };

  // Loading state
  if (loading) {
    return (
      <div className="quiz-container">
        <div className="loading">Loading quiz...</div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="quiz-container">
        <div className="error-state">
          <h2>Oops!</h2>
          <p>{error}</p>
          <button className="btn btn-primary" onClick={() => navigate('/')}>
            Go Home
          </button>
        </div>
      </div>
    );
  }

  // Quiz complete state
  if (quizComplete && finalResults) {
    const percentage = Math.round((finalResults.score / finalResults.total) * 100);
    
    return (
      <div className="quiz-container">
        <div className="quiz-complete">
          <div className="complete-icon">
            {percentage >= 70 ? '🎉' : percentage >= 50 ? '👍' : '💪'}
          </div>
          <h2>Quiz Complete!</h2>
          <div className="final-score">
            <span className="score-number">{finalResults.score}</span>
            <span className="score-divider">/</span>
            <span className="score-total">{finalResults.total}</span>
          </div>
          <div className="score-percentage">{percentage}%</div>
          <div className="complete-message">
            {percentage >= 70 ? 'Excellent work!' : percentage >= 50 ? 'Good effort!' : 'Keep practicing!'}
          </div>
          
          {/* Results breakdown */}
          <div className="results-breakdown">
            <h4>Results</h4>
            {finalResults.results.map((result, index) => (
              <div 
                key={result.question_id} 
                className={`result-item ${result.correct ? 'correct' : 'incorrect'}`}
              >
                <span className="result-number">Q{index + 1}</span>
                <span className="result-status">
                  {result.correct ? '✓' : '✗'}
                </span>
                {!result.correct && (
                  <span className="result-answer">
                    Correct: {result.correct_answer}
                  </span>
                )}
              </div>
            ))}
          </div>
          
          <div className="complete-buttons">
            <button className="btn btn-primary" onClick={handleRestart}>
              Try Again
            </button>
            <button className="btn btn-secondary" onClick={() => navigate('/')}>
              Home
            </button>
          </div>
        </div>
      </div>
    );
  }

  const currentQ = questions[currentQuestionIndex];

  return (
    <div className="quiz-container">
      <div className="quiz-header">
        <button className="btn btn-back" onClick={() => navigate('/')}>
          &larr; Exit
        </button>
        <div className="quiz-progress">
          <span>{currentQuestionIndex + 1} / {questions.length}</span>
          <div className="progress-bar">
            <div 
              className="progress-fill" 
              style={{ width: `${((currentQuestionIndex + 1) / questions.length) * 100}%` }}
            />
          </div>
        </div>
        <div className="quiz-title-small">{quiz.title}</div>
      </div>

      <div className="quiz-card">
        <div className="quiz-question">{currentQ.question}</div>
        
        <div className="quiz-options">
          {[
            { letter: 'A', text: currentQ.option_a },
            { letter: 'B', text: currentQ.option_b },
            { letter: 'C', text: currentQ.option_c },
            { letter: 'D', text: currentQ.option_d }
          ].map(({ letter, text }) => (
            <button
              key={letter}
              className={`quiz-option ${selectedAnswer === letter ? 'selected' : ''} ${getOptionClass(letter)}`}
              onClick={() => handleAnswerSelect(letter)}
              disabled={showResult}
            >
              <span className="option-letter">{letter}</span>
              <span className="option-text">{text}</span>
              {showResult && currentResult && letter === currentResult.correct_answer && (
                <span className="result-icon">✓</span>
              )}
              {showResult && currentResult && letter === selectedAnswer && letter !== currentResult.correct_answer && (
                <span className="result-icon">✗</span>
              )}
            </button>
          ))}
        </div>

        {showResult && currentResult && (
          <div className={`feedback ${currentResult.correct ? 'correct' : 'incorrect'}`}>
            {currentResult.correct 
              ? '🎉 Correct!' 
              : `❌ Wrong! The answer was ${currentResult.correct_answer}`
            }
          </div>
        )}

        {!showResult ? (
          <button 
            className="btn btn-primary btn-full" 
            onClick={handleSubmitAnswer}
            disabled={!selectedAnswer || checking}
          >
            {checking ? 'Checking...' : 'Submit Answer'}
          </button>
        ) : (
          <button 
            className="btn btn-primary btn-full" 
            onClick={handleNextQuestion}
          >
            {currentQuestionIndex < questions.length - 1 ? 'Next Question →' : 'See Results'}
          </button>
        )}
      </div>
    </div>
  );
}

export default PlayQuiz;
